import { ref, type Ref } from 'vue'
import { toast } from '@/components/ui/toast'

export interface StoreActionOptions {
  loadingKey?: string
  successMessage?: string
  errorMessage?: string
  showSuccessToast?: boolean
  showErrorToast?: boolean
}

export interface StoreActionResult<T> {
  execute: () => Promise<T | null>
  isLoading: Ref<boolean>
  error: Ref<string | null>
  data: Ref<T | null>
}

export function useStoreAction<T>(
  actionFn: () => Promise<{ success: boolean; error?: string; data?: T }>,
  options: StoreActionOptions = {}
): StoreActionResult<T> {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true
  } = options

  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const data = ref<T | null>(null) as Ref<T | null>

  const execute = async (): Promise<T | null> => {
    isLoading.value = true
    error.value = null

    try {
      const result = await actionFn()

      if (result.success) {
        if (result.data !== undefined) {
          data.value = result.data
        }
        
        if (successMessage && showSuccessToast) {
          toast({
            title: successMessage
          })
        }
        
        return result.data || null
      } else {
        const errorMsg = result.error || errorMessage || 'Operation failed'
        error.value = errorMsg
        
        if (showErrorToast) {
          toast({
            title: errorMsg,
            variant: "destructive"
          })
        }
        
        return null
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unexpected error'
      error.value = errorMsg
      
      if (showErrorToast) {
        toast({
          title: errorMsg,
          variant: "destructive"
        })
      }
      
      return null
    } finally {
      isLoading.value = false
    }
  }

  return {
    execute,
    isLoading,
    error,
    data
  }
}